import type { ReviewResult, SynthesisReference } from './serviceExecutionApi';
import { studyGroups } from './reviewStudies';

export function synthesisSources(result: ReviewResult) {
  return studyGroups(result).flatMap((group) => group.reports.flatMap((record) => (record.findings || []).map((finding) => ({ group: group.id, record, reference: { record_id: record.id, ...finding } }))));
}

export function sameReference(a: SynthesisReference, b: SynthesisReference) {
  return a.record_id === b.record_id && a.field === b.field && a.value === b.value && a.fragment_id === b.fragment_id && a.quote === b.quote;
}

export function synthesisComplete(result: ReviewResult) {
  const synthesis = result.synthesis;
  if (!synthesis || !synthesis.approach.trim() || !synthesis.limitations.trim() || !synthesis.conclusion.trim()) return false;
  const sources = synthesisSources(result);
  const groups = studyGroups(result);
  const addressed = new Set<string>();
  for (const theme of synthesis.themes) {
    if (!theme.title.trim() || !theme.text.trim() || !theme.references.length) return false;
    if (theme.references.some((ref, index) => theme.references.slice(0, index).some((other) => sameReference(ref, other)))) return false;
    for (const reference of theme.references) {
      const source = sources.find((item) => sameReference(item.reference, reference));
      if (!source) return false;
      addressed.add(source.group);
    }
  }
  if (Object.entries(synthesis.omitted_studies).some(([key, reason]) => !groups.some((group) => group.id === key) || !reason.trim() || addressed.has(key))) return false;
  return groups.every((group) => addressed.has(group.id) || Object.prototype.hasOwnProperty.call(synthesis.omitted_studies, group.id));
}

export function synthesisReady(result: ReviewResult) {
  return result.config.narrative_synthesis ? !!result.synthesis?.verified && synthesisComplete(result) : !result.synthesis;
}

export function invalidateSynthesis(before: ReviewResult, after: ReviewResult): ReviewResult {
  const basis = (result: ReviewResult) => JSON.stringify({ groups: studyGroups(result).map((group) => ({ id: group.id, resolution: group.resolution, reports: group.reports.map((record) => ({ id: record.id, findings: record.findings || [] })) })), quality: result.quality_assessments || {} });
  if (!after.synthesis || basis(before) === basis(after)) return after;
  const sources = synthesisSources(after);
  const groups = new Set(studyGroups(after).map((group) => group.id));
  return { ...after, synthesis: { ...after.synthesis, verified: false,
    themes: after.synthesis.themes.map((theme) => ({ ...theme, references: theme.references.filter((reference) => sources.some((source) => sameReference(source.reference, reference))) })),
    omitted_studies: Object.fromEntries(Object.entries(after.synthesis.omitted_studies).filter(([key]) => groups.has(key))) } };
}
